import React, { useState, useCallback, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { DropResult } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { Button, Flex, Input, Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { VisitorCustomFieldType } from 'graphql-query-contracts';
import Dropdown from 'components/form/Dropdown';
import Label from 'components/general/Label';
import { CustomFieldRenderer } from '../../../../feature/components/CustomFieldsSection/CustomFieldRenderer';
import type {
  CustomFieldFormData,
  CustomFieldOption,
  CustomFieldOptionRow,
} from '../../../../feature/components/CustomFieldsSection/customField.types';
import {
  VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS,
  coerceVisitorCustomFieldTypeFromUi,
  isVisitorCustomFieldPlaceholderSupported,
  isVisitorCustomFieldSelectType,
} from '../../../../feature/components/CustomFieldsSection/customField.constants';

import { CustomFieldModalOptionRowsEditor } from './CustomFieldModalOptionRowsEditor';
import styles from './CustomFieldModal.scss';

const DEFAULT_FIELD_TYPE = VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS[0].value;

export type CustomFieldModalProps = {
  onClose: () => void;
  /**
   * Resolves true when the field was persisted; false when save failed without throwing.
   * The modal shows a generic save-failed message; return false or throw — callers may still use toasts for specific errors.
   */
  onSave: (data: CustomFieldFormData) => Promise<boolean>;
  onUnsavedDraftChange?: (hasUnsavedDraft: boolean) => void;
};

type ValidateFormParams = {
  fieldName: string;
  fieldKey: string;
  fieldType: VisitorCustomFieldType;
  optionRows: CustomFieldOptionRow[];
  t: (key: string) => string;
};

type UseCustomFieldFormParams = {
  onClose: () => void;
  onSave: (data: CustomFieldFormData) => Promise<boolean>;
  t: (key: string) => string;
};

const NAME_MAX_LENGTH = 256;
const REQUIRED_ASTERISK = '*';

function deriveFieldKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getOptionsForSave(
  fieldType: VisitorCustomFieldType,
  optionRows: CustomFieldOptionRow[]
): CustomFieldOption[] {
  if (!isVisitorCustomFieldSelectType(fieldType)) return [];

  return optionRows
    .filter(r => r.displayText.trim() !== '' && r.value.trim() !== '')
    .map(r => ({ displayText: r.displayText.trim(), value: r.value.trim() }));
}

type PersistUiState = { saving: boolean; submitError: string };

type RunCustomFieldSaveParams = {
  validate: () => boolean;
  setPersistUi: Dispatch<SetStateAction<PersistUiState>>;
  fieldType: VisitorCustomFieldType;
  optionRows: CustomFieldOptionRow[];
  fieldName: string;
  fieldKey: string;
  placeholder: string;
  onSave: (data: CustomFieldFormData) => Promise<boolean>;
  onClose: () => void;
  t: (key: string) => string;
};

async function runCustomFieldSave(params: RunCustomFieldSaveParams): Promise<void> {
  params.setPersistUi(s => ({ ...s, submitError: '' }));

  if (!params.validate()) return;

  const saveFailedMessage = params.t('web.admin.visitorManagement.customField.form.saveFailed');

  const setSubmitSaveFailed = (): void => {
    params.setPersistUi(s => ({ ...s, submitError: saveFailedMessage }));
  };

  params.setPersistUi(s => ({ ...s, saving: true }));

  try {
    const options = getOptionsForSave(params.fieldType, params.optionRows);
    const saved = await params.onSave({
      fieldName: params.fieldName.trim(),
      fieldKey: params.fieldKey.trim(),
      fieldType: params.fieldType,
      options,
      placeholder:
        isVisitorCustomFieldPlaceholderSupported(params.fieldType) && params.placeholder.trim()
          ? params.placeholder.trim()
          : undefined,
    });

    if (saved) {
      params.onClose();
    } else {
      setSubmitSaveFailed();
    }
  } catch {
    setSubmitSaveFailed();
  } finally {
    params.setPersistUi(s => ({ ...s, saving: false }));
  }
}

function reorderOptionRows(
  rows: CustomFieldOptionRow[],
  startIndex: number,
  endIndex: number
): CustomFieldOptionRow[] {
  const next = [...rows];
  const [removed] = next.splice(startIndex, 1);

  next.splice(endIndex, 0, removed);

  return next;
}

function validateForm(params: ValidateFormParams): Record<string, string> {
  const { fieldName, fieldKey, fieldType, optionRows, t } = params;
  const next: Record<string, string> = {};

  if (!fieldName.trim()) {
    next.fieldName = t('web.admin.visitorManagement.customField.form.validation.nameRequired');
  } else if (!fieldKey.trim() || !/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(fieldKey)) {
    next.fieldName = t('web.admin.visitorManagement.customField.form.validation.invalidFieldName');
  }

  if (isVisitorCustomFieldSelectType(fieldType)) {
    // Reject half-filled rows: exactly one of displayText / value is non-empty.
    const hasInvalidOption = optionRows.some(r => {
      const displayFilled = r.displayText.trim() !== '';
      const valueFilled = r.value.trim() !== '';

      return displayFilled !== valueFilled;
    });
    const hasValidOption = optionRows.some(
      r => r.displayText.trim() !== '' && r.value.trim() !== ''
    );

    if (hasInvalidOption) {
      next.options = t('web.admin.visitorManagement.customField.form.validation.optionsBothRequired');
    } else if (!hasValidOption) {
      next.options = t('web.admin.visitorManagement.customField.form.validation.optionsRequired');
    }
  }

  return next;
}

function clearStaleOptionsError(
  setErrors: Dispatch<SetStateAction<Record<string, string>>>
): void {
  setErrors(prev => (prev.options ? { ...prev, options: '' } : prev));
}

function useCustomFieldForm({ onClose, onSave, t }: UseCustomFieldFormParams) {
  const [fieldName, setFieldName] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldType, setFieldType] = useState<VisitorCustomFieldType>(DEFAULT_FIELD_TYPE);
  const [optionRows, setOptionRows] = useState<CustomFieldOptionRow[]>([]);
  const [placeholder, setPlaceholder] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [persistUi, setPersistUi] = useState<PersistUiState>({ saving: false, submitError: '' });

  const handleNameChange = useCallback((value: string) => {
    const v = value.replace(/^\s+/, '').slice(0, NAME_MAX_LENGTH);

    setFieldName(v);
    setFieldKey(deriveFieldKey(v));
    setErrors(prev => (prev.fieldName ? { ...prev, fieldName: '' } : prev));
  }, []);

  const handleFieldTypeChange = useCallback((value: string) => {
    const next = coerceVisitorCustomFieldTypeFromUi(value);

    setFieldType(next);

    if (!isVisitorCustomFieldSelectType(next)) {
      setOptionRows([]);
      clearStaleOptionsError(setErrors);
    }

    if (!isVisitorCustomFieldPlaceholderSupported(next)) {
      setPlaceholder('');
    }
  }, []);

  const addOption = useCallback(() => {
    setOptionRows(prev => [...prev, { id: uuid(), displayText: '', value: '' }]);
  }, []);

  const updateOption = useCallback((id: string, update: Partial<Pick<CustomFieldOptionRow, 'displayText' | 'value'>>) => {
    setOptionRows(prev =>
      prev.map(row => (row.id === id ? { ...row, ...update } : row))
    );
    clearStaleOptionsError(setErrors);
  }, []);

  const removeOption = useCallback((id: string) => {
    setOptionRows(prev => prev.filter(row => row.id !== id));
    clearStaleOptionsError(setErrors);
  }, []);

  const onDragEnd = useCallback((result: DropResult) => {
    const destination = result.destination;

    if (!destination) return;

    setOptionRows(prev =>
      reorderOptionRows(prev, result.source.index, destination.index)
    );
  }, []);

  const handlePlaceholderChange = useCallback((value: string) => {
    setPlaceholder(value);
  }, []);

  const validate = useCallback((): boolean => {
    const next = validateForm({ fieldName, fieldKey, fieldType, optionRows, t });

    setErrors(next);

    return Object.keys(next).length === 0;
  }, [fieldName, fieldKey, fieldType, optionRows, t]);

  const handleSubmit = useCallback(
    () =>
      runCustomFieldSave({
        validate,
        setPersistUi,
        fieldType,
        optionRows,
        fieldName,
        fieldKey,
        placeholder,
        onSave,
        onClose,
        t,
      }),
    [
      fieldName,
      fieldKey,
      fieldType,
      optionRows,
      placeholder,
      t,
      validate,
      onSave,
      onClose,
    ]
  );

  const isSelectType = isVisitorCustomFieldSelectType(fieldType);
  const previewField = useMemo(() => {
    const previewOptions = isVisitorCustomFieldSelectType(fieldType)
      ? getOptionsForSave(fieldType, optionRows)
      : null;

    return {
      id: 'preview',
      organizationId: '',
      fieldKey: fieldKey || 'preview',
      fieldName: fieldName || t('web.admin.content.metatag.useMetatagEdit.newData'),
      fieldType,
      isEnabled: true,
      options: previewOptions,
      placeholder:
        isVisitorCustomFieldPlaceholderSupported(fieldType) && placeholder
          ? placeholder
          : undefined,
      createdAtDatetime: undefined,
      updatedAtDatetime: undefined,
      createdByUserId: '',
      updatedByUserId: '',
    };
  }, [fieldKey, fieldName, fieldType, optionRows, placeholder, t]);

  const isDirty = useMemo(
    () =>
      fieldName.trim().length > 0 ||
      placeholder.trim().length > 0 ||
      optionRows.length > 0 ||
      fieldType !== DEFAULT_FIELD_TYPE,
    [fieldName, fieldType, optionRows, placeholder]
  );

  return {
    fieldName,
    fieldKey,
    fieldType,
    optionRows,
    placeholder,
    errors,
    submitError: persistUi.submitError,
    saving: persistUi.saving,
    handleNameChange,
    handleFieldTypeChange,
    addOption,
    updateOption,
    removeOption,
    onDragEnd,
    handleSubmit,
    isSelectType,
    previewField,
    handlePlaceholderChange,
    onClose,
    t,
    isDirty,
  };
}

export function CustomFieldModal({
  onClose,
  onSave,
  onUnsavedDraftChange,
}: CustomFieldModalProps): React.ReactElement {
  const { t } = useTranslation();

  const form = useCustomFieldForm({ onClose, onSave, t });
  const fieldTypeItems = useMemo(
    () => VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS.map(ft => ({ value: ft.value, label: t(ft.labelKey) })),
    [t]
  );

  useEffect(() => {
    onUnsavedDraftChange?.(form.isDirty);
  }, [form.isDirty, onUnsavedDraftChange]);

  return (
    <div className={styles.modalRoot}>
      <div className={styles.entry}>
        <fieldset className={styles.field} data-test="input-type-dropdown">
          <Label h1>
            {form.t('web.admin.content.friendlyProperty.labels.inputType')}
          </Label>
          <Dropdown
            className={styles.fieldTypeDropdown}
            placeholder={form.t('web.admin.content.metatag.edit.fieldType.fieldTypeInputPlaceholder')}
            items={fieldTypeItems}
            value={form.fieldType}
            onValueChange={form.handleFieldTypeChange}
            doTranslation={false}
          />
        </fieldset>

        <fieldset className={styles.field} data-test="property-name">
          <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.name.tooltip')}>
            {form.t('web.admin.content.friendlyProperty.labels.name')}
            <span className={styles.required}>{REQUIRED_ASTERISK}</span>
          </Label>
          <Input
            value={form.fieldName}
            onChange={form.handleNameChange}
            placeholder={form.t('web.admin.content.friendlyProperty.placeholder.name')}
            aria-invalid={Boolean(form.errors.fieldName)}
          />
          {form.errors.fieldName && <span className={styles.fieldError}>{form.errors.fieldName}</span>}
        </fieldset>

        {form.isSelectType && (
          <fieldset className={styles.field} data-test="dropdown-options">
            <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.modal.options.description')}>
              {form.t('web.admin.content.friendlyProperty.labels.options')}
              <span className={styles.required}>{REQUIRED_ASTERISK}</span>
              <Icon
                name="plus-circle"
                set={ICON_SET_FONTAWESOME}
                className={styles.addIcon}
                onClick={form.addOption}
              />
            </Label>
            <CustomFieldModalOptionRowsEditor
              optionRows={form.optionRows}
              onUpdate={form.updateOption}
              onRemove={form.removeOption}
              onDragEnd={form.onDragEnd}
              optionsError={form.errors.options}
              t={form.t}
            />
          </fieldset>
        )}

        {isVisitorCustomFieldPlaceholderSupported(form.fieldType) && (
          <fieldset className={styles.field} data-test="custom-field-placeholder">
            <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.placeholder.tooltip')}>
              {form.t('web.admin.content.friendlyProperty.labels.placeholder')}
            </Label>
            <Input
              value={form.placeholder}
              onChange={form.handlePlaceholderChange}
              placeholder={form.t('web.admin.visitorManagement.customField.modal.placeholderPlaceholder')}
            />
          </fieldset>
        )}

        {form.submitError && (
          <span className={styles.fieldError} role="alert">
            {form.submitError}
          </span>
        )}
        <Flex gap={2} justify="end" className={styles.footerActions}>
          <Button variant="secondary" onClick={form.onClose} disabled={form.saving}>
            {form.t('web.admin.visitorManagement.customField.modal.cancel')}
          </Button>
          <Button variant="primary" onClick={form.handleSubmit} disabled={form.saving} loading={form.saving}>
            {form.t('web.admin.visitorManagement.customField.modal.create')}
          </Button>
        </Flex>
      </div>

      <div className={styles.preview} data-test="preview-container">
        <fieldset className={styles.field}>
          <Label h1 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.preview.tooltip')}>
            {form.t('web.admin.content.friendlyProperty.labels.preview')}
          </Label>
        </fieldset>
        <CustomFieldRenderer field={form.previewField} disabled={false} />
      </div>
    </div>
  );
}
